<template>
  <div class="teacher-dashboard pa-4">
    <div class="teacher-wrap">
      <div class="d-flex align-center justify-space-between mb-8">
        <div class="d-flex align-center">
          <v-btn icon color="white" to="/akademie" class="mr-4">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <h2 class="text-h4 font-weight-bold white--text">Teacher Dashboard</h2>
            <div class="grey--text">Übersicht aller Lernenden und deren Fortschritt</div>
          </div>
        </div>
        <v-chip color="blue" text-color="white" class="font-weight-bold">Teacher-Bereich</v-chip>
      </div>

      <h3 class="text-h5 font-weight-bold white--text mb-4 d-flex align-center">
        <v-icon color="#D4AF37" class="mr-2">mdi-calendar-check</v-icon>
        Team-Schulungsmanagement
      </h3>

      <v-row class="mb-8">
        <v-col v-if="loadingTermine" cols="12" class="text-center">
          <v-progress-circular indeterminate color="#D4AF37"></v-progress-circular>
        </v-col>

        <v-col v-else-if="upcomingTermine.length === 0" cols="12">
          <v-alert outlined color="grey" class="rounded-xl"> Aktuell sind keine kommenden Schulungen geplant. </v-alert>
        </v-col>

        <v-col v-for="termin in upcomingTermine" :key="termin._id" cols="12" md="6" lg="4">
          <v-card class="termin-manage-card rounded-xl pa-4" dark elevation="0">
            <div class="d-flex justify-space-between align-start mb-2">
              <div class="date-badge-small">
                <div class="text-h6 font-weight-bold line-height-1">{{ formatDateDay(termin.startsAt) }}</div>
                <div class="text-caption gold-text">{{ formatDateMonth(termin.startsAt) }}</div>
              </div>
              <v-chip x-small outlined color="#D4AF37">{{ termin.location || "Ort offen" }}</v-chip>
            </div>

            <h4 class="text-subtitle-1 font-weight-bold white--text mb-3 text-truncate">{{ termin.title }}</h4>

            <div class="d-flex align-center justify-space-between mt-auto">
              <div class="text-caption grey--text">
                <v-icon x-small color="grey">mdi-account-multiple</v-icon>
                {{ termin.registeredCount || 0 }}/{{ termin.maxParticipants }} Plätze
              </div>
              <v-btn small color="#D4AF37" class="black--text font-weight-bold" rounded @click="openTeamManager(termin)"> Team verwalten </v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-divider class="mb-8 border-white-05"></v-divider>

      <h3 class="text-h5 font-weight-bold white--text mb-4">Mitarbeiter-Übersicht</h3>
      <v-card dark class="pa-6 rounded-xl" style="background: rgba(30, 30, 30, 0.8)">
        <v-row class="mb-4">
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Name oder Studio suchen..."
              outlined
              dense
              hide-details
              color="#D4AF37"></v-text-field>
          </v-col>
          <v-col cols="12" sm="4">
            <v-select
              v-model="tierFilter"
              :items="['Alle', 'Bronze', 'Silber', 'Gold']"
              label="Höchste Stufe"
              outlined
              dense
              hide-details
              color="#D4AF37"></v-select>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="filteredUsers"
          :loading="loading"
          :search="search"
          :items-per-page="25"
          dark
          class="elevation-0 transparent-bg"
          @click:row="openKartei">
          <template v-slot:item.name="{ item }">
            <div class="d-flex align-center font-weight-bold">
              <v-avatar size="32" color="grey darken-3" class="mr-3">
                <span class="white--text text-caption"
                  >{{ item.firstName ? item.firstName.charAt(0) : "" }}{{ item.lastName ? item.lastName.charAt(0) : "" }}</span
                >
              </v-avatar>
              {{ item.firstName }} {{ item.lastName }}
            </div>
          </template>
          <template v-slot:item.highestTier="{ item }">
            <v-chip :color="getTierColor(item.highestTier)" small text-color="black" class="font-weight-bold">
              {{ item.highestTier || "Keine" }}
            </v-chip>
          </template>
          <template v-slot:item.progress="{ item }">
            <div class="d-flex align-center">
              <v-progress-linear :value="item.totalProgress" color="#D4AF37" rounded height="6" class="mr-2" style="width: 80px"></v-progress-linear>
              <span class="text-caption gold-text">{{ item.totalProgress }}%</span>
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon color="#D4AF37" @click.stop="openKartei(item)">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <TeamRegistrationDialog ref="teamDialog" @refresh="loadTermine" />
  </div>
</template>

<script>
import api from "@/api"
import TeamRegistrationDialog from "./TeamRegistrationDialog.vue"

export default {
  name: "TeacherDashboard",
  components: {
    TeamRegistrationDialog,
  },
  data() {
    return {
      users: [],
      upcomingTermine: [],
      loading: false,
      loadingTermine: false,
      search: "",
      tierFilter: "Alle",
      headers: [
        { text: "Name", value: "name" },
        { text: "Studio", value: "studio" },
        { text: "Rolle", value: "role" },
        { text: "Höchste Stufe", value: "highestTier" },
        { text: "Gesamtfortschritt", value: "progress" },
        { text: "", value: "actions", sortable: false, align: "right" },
      ],
    }
  },
  computed: {
    filteredUsers() {
      if (this.tierFilter === "Alle") return this.users
      return this.users.filter(
        (u) => (u.highestTier && u.highestTier.toLowerCase() === this.tierFilter.toLowerCase()) || (!u.highestTier && this.tierFilter === "Keine"),
      )
    },
  },
  mounted() {
    this.loadUsers()
    this.loadTermine()
  },
  methods: {
    async loadUsers() {
      this.loading = true
      try {
        const res = await api.akademie.fetchTeacherUserList()
        this.users = res || []
      } catch (e) {
        console.error("Fehler beim Laden der Mitarbeiter-Liste:", e)
      } finally {
        this.loading = false
      }
    },
    async loadTermine() {
      this.loadingTermine = true
      try {
        const res = await api.akademie.fetchTermine()
        const now = new Date()
        // Nur zukünftige Termine anzeigen und nach Datum sortieren
        this.upcomingTermine = (res || []).filter((t) => new Date(t.startsAt) >= now).sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
      } catch (e) {
        console.error("Fehler beim Laden der Termine:", e)
      } finally {
        this.loadingTermine = false
      }
    },
    getTierColor(tier) {
      if (!tier) return "grey"
      const t = tier.toLowerCase()
      if (t === "bronze") return "#CD7F32"
      if (t === "silber") return "#C0C0C0"
      if (t === "gold") return "#FFD700"
      return "grey"
    },
    openKartei(item) {
      this.$router.push(`/akademie-teacher/user/${item._id || item.id}`)
    },
    openTeamManager(termin) {
      if (this.$refs.teamDialog) {
        this.$refs.teamDialog.open(termin)
      }
    },
    formatDateDay(d) {
      return d ? new Date(d).getDate() : ""
    },
    formatDateMonth(d) {
      if (!d) return ""
      return new Date(d).toLocaleDateString("de-CH", { month: "short" }).replace(".", "").toUpperCase()
    },
  },
}
</script>

<style scoped>
.teacher-dashboard {
  min-height: 100vh;
  background-color: #121212;
}
.teacher-wrap {
  max-width: 1400px;
  margin: 0 auto;
}
.transparent-bg {
  background-color: transparent !important;
}
.gold-text {
  color: #d4af37 !important;
}
.v-data-table :deep(tr) {
  cursor: pointer;
  transition: background-color 0.2s;
}
.v-data-table :deep(tr:hover) {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

.termin-manage-card {
  background: rgba(40, 40, 40, 0.6) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: transform 0.2s, border-color 0.2s;
  display: flex;
  flex-direction: column;
  height: 100%;
}
.termin-manage-card:hover {
  transform: translateY(-2px);
  border-color: rgba(212, 175, 55, 0.3);
}
.date-badge-small {
  background: rgba(212, 175, 55, 0.1);
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 8px;
  padding: 4px 8px;
  text-align: center;
  min-width: 45px;
}
.line-height-1 {
  line-height: 1;
}
.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}
</style>
